import { ChevronDown, CircleCheck, Pencil, PlaneTakeoff } from '@/components/icons'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Pill } from '@/components/ui/pill'
import { hapticLight } from '@/lib/haptics'
import { useTripSetState } from '@/lib/hooks'
import { toStatusBadge, type TripState } from '@/lib/tripState'

interface TripStatusSelectProps {
  tripId: string
  tripState: TripState
  /** Validated Draft → Scheduled transition (from useTripStatusActions). */
  onRequestSchedule: () => void
  /** Validated → Complete transition with confirmation (from useTripStatusActions). */
  onRequestComplete: () => void
  isPending?: boolean
}

const pillVariants = { draft: 'draft', scheduled: 'info', complete: 'success' } as const
const labels: Record<TripState, string> = {
  Draft: 'Draft',
  Scheduled: 'Scheduled',
  Complete: 'Complete',
}
const icons: Record<TripState, React.ComponentType<{ className?: string }>> = {
  Draft: Pencil,
  Scheduled: PlaneTakeoff,
  Complete: CircleCheck,
}

// Trips move one step at a time — no direct Draft ↔ Complete jumps.
const transitions: Record<TripState, TripState[]> = {
  Draft: ['Scheduled'],
  Scheduled: ['Draft', 'Complete'],
  Complete: ['Scheduled'],
}

/**
 * Trip status pill that doubles as a dropdown to switch the trip between
 * Draft / Scheduled / Complete. Forward transitions go through the validated
 * useTripStatusActions callbacks (owned by the parent, which also renders the
 * confirmation dialogs); backward transitions apply immediately.
 */
export function TripStatusSelect({
  tripId,
  tripState,
  onRequestSchedule,
  onRequestComplete,
  isPending = false,
}: TripStatusSelectProps) {
  const { mutate: setTripState, isPending: isSetPending } = useTripSetState()
  const pending = isPending || isSetPending

  const moveBackTo = (target: 'Draft' | 'Scheduled') => {
    setTripState(
      { id: tripId, tripState: target },
      {
        onSuccess: () =>
          toast.success(target === 'Draft' ? 'Trip moved to draft' : 'Trip moved to scheduled'),
        onError: () => toast.error('Failed to change trip state'),
      },
    )
  }

  const handleSelect = (target: TripState) => {
    hapticLight()
    if (target === 'Complete') {
      onRequestComplete()
    } else if (target === 'Scheduled' && tripState === 'Draft') {
      onRequestSchedule()
    } else {
      moveBackTo(target as 'Draft' | 'Scheduled')
    }
  }

  const targets = transitions[tripState]
  const status = toStatusBadge(tripState)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={pending}
        onPointerDown={() => hapticLight()}
        className="rounded-full outline-none disabled:opacity-70"
      >
        <Pill
          variant={pillVariants[status]}
          className={status === 'draft' ? 'bg-content-primary-subtle-light' : undefined}
        >
          {labels[tripState]}
          <ChevronDown />
        </Pill>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[141px] rounded-lg p-0.5">
        {targets.map((target) => {
          const Icon = icons[target]
          return (
            <DropdownMenuItem
              key={target}
              className="min-h-8 px-2 text-sm font-semibold text-content-primary-bold focus:text-content-primary-bold"
              onClick={() => handleSelect(target)}
            >
              <Icon className="size-4 text-content-primary-subtle" />
              {labels[target]}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
